<script lang="ts" setup>
  import { ref, type PropType } from 'vue';

  type Item = {
    header: string;
    products: Object[];
  }

  defineOptions({
    inheritAttrs: false,
  });

  const props = defineProps({
    items: { type: Array as PropType<Item[]>, required: true, default: [] },
    headerType: String,
    header: [String, null],
    enableAutoplay: Boolean,
    timeAutoplay: [Number, String],
    speed: [Number, String],
    id: { type: Number, required: true },
  });

  const activeTab = ref(props.items[0]?.header);

  const parameters = ref({
    breakpoints: {
      1: {
        slidesPerView: 1.5,
        spaceBetween: 10,
        slidesOffsetBefore: 10,
        slidesOffsetAfter: 10,
      },
      576: {
        slidesPerView: 2.5,
        spaceBetween: 10,
        slidesOffsetBefore: 10,
        slidesOffsetAfter: 10,
      },
      768: {
        slidesPerView: 3.5,
        spaceBetween: 10,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
      },
      992: {
        slidesPerView: 4,
        spaceBetween: 16,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
      },
      1400: {
        slidesPerView: 5,
        spaceBetween: 16,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
      },
    },
  });

  function getTabId(header: string): string {
    return `tab-${props.id}-${header?.replace(/\s+/g, '-').toLowerCase()}`;
  }

  function getPanelId(header: string): string {
    return `tabpanel-${props.id}-${header?.replace(/\s+/g, '-').toLowerCase()}`;
  }

  function setActiveTab(name: string) {
    activeTab.value = name;
  }

  function onKeydown(event: KeyboardEvent, index: number) {
    const tabs = props.items;
    let newIndex = index;

    if (event.key === 'ArrowRight') {
      newIndex = (index + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft') {
      newIndex = (index - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      newIndex = 0;
    } else if (event.key === 'End') {
      newIndex = tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();

    const newTab = tabs[newIndex];
    if (!newTab) return;
    setActiveTab(newTab.header);
    const tabEl = document.getElementById(getTabId(newTab.header));
    tabEl?.focus();
  }
</script>

<template>
  <SfSection v-if="items.length > 0" :headerType :title="header" class="products-tab-block">
    <div
      class="products-tab-block__buttons-wrapper custom-scrollbar"
      role="tablist"
      :aria-label="header || ''"
    >
      <div class="products-tab-block__buttons">
        <SfButton
          v-for="(item, index) in items"
          :key="index"
          :id="getTabId(item.header)"
          role="tab"
          :aria-selected="activeTab === item.header"
          :aria-controls="getPanelId(item.header)"
          :tabindex="activeTab === item.header ? 0 : -1"
          :primary="activeTab === item.header"
          @click="setActiveTab(item.header)"
          @keydown="onKeydown($event, index)"
        >
          {{ item.header }}
        </SfButton>
      </div>
    </div>
    <template v-for="(item, index) in items" :key="`carousel-${index}`">
      <div
        :id="getPanelId(item.header)"
        role="tabpanel"
        :aria-labelledby="getTabId(item.header)"
        :hidden="activeTab !== item.header"

      >
        <SfProductsCarousel
          v-if="item.products.length"
          :parameters
          :products="item.products"
          :enableAutoplay
          :timeAutoplay
          :speed
        />
      </div>
    </template>
  </SfSection>
</template>
<style lang="scss">
  :root {
    --products-tabs-block-buttons-wrap-border: 1px solid var(--secondary-border);
    --products-tabs-block-buttons-wrap-background: var(--secondary-background);
  }
</style>
<style lang="scss" scoped>
  .products-tab-block {
    --section-content-gap: 1rem;

    @media (max-width: 992px) {
      --section-content-gap: 0.625rem;
    }

    &__buttons-wrapper {
      overflow-x: auto;
      max-width: 100%;
    }

    &__buttons {
      display: flex;
      flex-wrap: nowrap;
      gap: 0.25rem;
      width: min-content;
      padding: 0.5rem;
      border-radius: var(--sf-btn-border-radius);
      background: var(--products-tabs-block-buttons-wrap-background);
      border: var(--products-tabs-block-buttons-wrap-border);

      @media (max-width: 767px) {
        margin: 0 0.625rem;
        gap: 0.25rem;
      }
    }
  }
</style>